import type { TemplateVariable } from '@/questionnaire/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  addTemplateVariable,
  getTemplate,
  getTemplateVariables,
  updateTemplate,
  updateTemplateVariable,
  getSteps,
  createStep,
  updateStep,
  getQuestions,
  updateQuestion,
  createQuestion,
  deleteTemplateVariable,
  deleteStep,
  deleteQuestion,
} from './services/template-service'
import type { ApiStep, ApiQuestion, Template } from './types'
import { findTemplate } from '@/utils/documentManipulationHelpers'

export const useAdminStore = defineStore('adminStore', () => {
  const templateVariables = ref<TemplateVariable[]>([])
  const templateVariablesMap = ref<Record<string, TemplateVariable>>({})
  const template = ref<Template>()
  const steps = ref<ApiStep[]>([])
  const questions = ref<ApiQuestion[]>([])

  const load = async (templateID: string) => {
    const res = await Promise.all([
      getTemplate(templateID),
      getTemplateVariables(templateID),
      getSteps(templateID),
      getQuestions(templateID),
    ])

    template.value = res[0]
    templateVariables.value = res[1]
    steps.value = res[2]
    questions.value = res[3]

    updateVariableMap()
  }

  const updateVariableMap = () => {
    if (templateVariables.value) {
      const TempTemplateVariablesMap = <Record<string, TemplateVariable>>{}
      templateVariables.value.forEach(({ code, id, ...rest }) => {
        if (code && id) {
          TempTemplateVariablesMap[code] = { code, id, ...rest }
        }
      })

      templateVariablesMap.value = TempTemplateVariablesMap
    }
  }
  const saveTemplate = async (): Promise<Template> => {
    if (!template.value) throw 'template not loaded'
    if (template.value?.definition) {
      const mandatorySections = template.value?.definition?.content?.filter(({ attrs }) => !attrs?.optional)

      if (mandatorySections?.length) {
        template.value.definition.templateVariablesInUse = findTemplate(mandatorySections, templateVariablesMap.value)
      }
    }

    template.value = await updateTemplate(template.value)

    return template.value
  }

  const saveTemplateVariable = async (templateVariable: TemplateVariable): Promise<TemplateVariable> => {
    if (!template.value?.id) {
      throw 'no template id available'
    }

    let tvar: TemplateVariable
    if (templateVariable.id) {
      const originalTemplateVariable = templateVariables.value.find(tv => tv.id === templateVariable.id)
      tvar = await updateTemplateVariable(template.value.id, templateVariable, originalTemplateVariable)
    } else {
      tvar = await addTemplateVariable(template.value.id, templateVariable)
    }
    templateVariables.value = await getTemplateVariables(template.value.id)
    updateVariableMap()
    return tvar
  }

  const removeTemplateVariable = async (templateVariableId: string | number) => {
    if (!template.value?.id) {
      throw 'no template id available'
    }

    const response = await deleteTemplateVariable(template.value.id, templateVariableId.toString())

    if (response.status == 204) {
      templateVariables.value = await getTemplateVariables(template.value.id)
      updateVariableMap()
    }
  }

  const saveStep = async (step: ApiStep) => {
    if (!template.value?.id) {
      throw 'no template id available'
    }
    let s: ApiStep
    if (step.id) {
      s = await updateStep(template.value.id, step)
    } else {
      s = await createStep(template.value.id, step)
    }

    steps.value = await getSteps(template.value.id)
    return s
  }

  const removeStep = async (stepId: string) => {
    if (!template.value?.id) {
      throw 'no template id available'
    }
    await deleteStep(template.value.id, stepId)
    steps.value = await getSteps(template.value.id)
  }

  const removeQuestion = async (questionId: string) => {
    if (!template.value?.id) {
      throw 'no template id available'
    }

    await deleteQuestion(template.value.id, questionId)
    questions.value = await getQuestions(template.value.id)
    steps.value = await getSteps(template.value.id)
  }

  const saveQuestion = async (question: ApiQuestion) => {
    if (!template.value?.id) {
      throw 'no template id available'
    }
    if (question.id) {
      await updateQuestion(template.value.id, question)
    } else {
      await createQuestion(template.value.id, question)
    }
    questions.value = await getQuestions(template.value.id)
  }
  return {
    // state
    templateVariables,
    template,
    steps,
    questions,

    // actions
    saveTemplate,
    load,
    saveTemplateVariable,
    saveStep,
    removeStep,
    removeQuestion,
    saveQuestion,
    removeTemplateVariable,
  }
})
